import { Camera, Calendar, MoreVertical, Edit2, Trash2, Share2, Upload, FolderDown } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { apiClient } from '../lib/api-client'
import { ALBUM_DRAG_MIME, parseAlbumDrag, serializeAlbumDrag } from '../lib/album-drag-payload'
import memoarLogo from '../assets/memoar-logo.png'
import type { Album } from "../../../../types"
import { albumRole } from '../lib/album-role'

interface AlbumCardProps {
  album: Album
  isDevelopment?: boolean
  onDragStart?: (album: Album) => void
  onDragEnd?: () => void
  onDoubleClick?: () => void
  onRename?: (newName: string) => void
  onDelete?: () => void
  onChangeDate?: (newDate: string) => void
  onShare?: (albumId: string) => void
  onDownload?: (albumId: string) => void
  onAlbumDrop?: (album: Album | null, targetAlbumId: string) => void
  onUpload?: (albumId: string) => void
  draggedAlbum?: Album | null
  isDragging?: boolean
  isEmpty?: boolean
  className?: string
}

export default function AlbumCard({
  album,
  isDevelopment = false,
  onDragStart,
  onDragEnd,
  onDoubleClick,
  onRename,
  onDelete,
  onChangeDate,
  onShare,
  onDownload,
  onAlbumDrop,
  onUpload: _onUpload,
  draggedAlbum,
  isDragging = false,
  isEmpty = false
}: AlbumCardProps): JSX.Element {
  const [showMenu, setShowMenu] = useState(false)
  const [isRenaming, setIsRenaming] = useState(false)
  const [isChangingDate, setIsChangingDate] = useState(false)
  const [newName, setNewName] = useState(album.name)
  const [newDate, setNewDate] = useState('')
  const menuRef = useRef<HTMLDivElement>(null)

  // Convert ISO string to YYYY-MM-DD format for input
  const getDateForInput = (isoString: string): string => {
    return isoString.split('T')[0]
  }

  useEffect(() => {
    setNewName(album.name)
    setNewDate(getDateForInput(album.createdAt))
  }, [album.name, album.createdAt])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMenu])

  // The Memoir's owner (SOL-185), as the server says; a temp album is the caller's.
  const isOwner = albumRole(album) === 'owner'

  const coverFile = album.resolvedCoverFileId
    ? album.files?.find((f) => f.id === album.resolvedCoverFileId)
    : undefined
  const coverUrl = !isEmpty && coverFile
    ? apiClient.getVariantUrl(album.uuid, coverFile, 'cover')
    : null

  // The cover fades in over a dark base + swept Memoar mark once it loads.
  // A cached cover (edge/browser) is already `complete` on mount, so it shows
  // instantly with no sweep; a cold one plays the reveal until it arrives.
  const [coverLoaded, setCoverLoaded] = useState(false)
  const coverImgRef = useRef<HTMLImageElement>(null)
  useEffect(() => {
    setCoverLoaded(false)
    const img = coverImgRef.current
    if (img && img.complete && img.naturalWidth > 0) setCoverLoaded(true)
  }, [coverUrl])

  const handleRenameSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (newName.trim() && newName !== album.name && onRename) {
      onRename(newName.trim())
    }
    setIsRenaming(false)
  }

  const handleRenameCancel = () => {
    setNewName(album.name)
    setIsRenaming(false)
  }

  const handleDateSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (newDate && newDate !== getDateForInput(album.createdAt) && onChangeDate) {
      onChangeDate(newDate)
    }
    setIsChangingDate(false)
  }

  const handleDateBlur = () => {
    if (newDate && newDate !== getDateForInput(album.createdAt) && onChangeDate) {
      onChangeDate(newDate)
    }
    setIsChangingDate(false)
  }

  const handleDateCancel = () => {
    setNewDate(getDateForInput(album.createdAt))
    setIsChangingDate(false)
  }

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowMenu(!showMenu)
  }

  const handleRename = () => {
    setIsRenaming(true)
    setShowMenu(false)
  }

  const handleChangeDate = () => {
    setIsChangingDate(true)
    setShowMenu(false)
  }

  const handleShare = () => {
    if (onShare) {
      onShare(album.uuid)
    }
    setShowMenu(false)
  }

  const handleDownload = () => {
    if (onDownload) {
      onDownload(album.uuid)
    }
    setShowMenu(false)
  }

  // Check if this is a temporary local album
  const isTempAlbum = album.uuid.startsWith('temp_')

  const handleDragStart = (e: React.DragEvent) => {
    if (isRenaming || isChangingDate) {
      e.preventDefault()
      return
    }

    // Store album data in dataTransfer for cross-component drops. The shape
    // is owned by lib/album-drag-payload (SOL-192) -- Breadcrumbs reads it
    // with the matching parser, and nothing type-checks that pair.
    e.dataTransfer.setData(ALBUM_DRAG_MIME, serializeAlbumDrag(album))
    e.dataTransfer.effectAllowed = 'move'
    
    if (onDragStart) {
      onDragStart(album)
    }
  }

  const handleDragEnd = () => {
    if (onDragEnd) {
      onDragEnd()
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    if (draggedAlbum && draggedAlbum.uuid !== album.uuid) {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
    }
  }

  const handleDragLeave = () => {
    // Optional: Add visual feedback for drag leave
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    
    if (!onAlbumDrop) return

    // Prefer the album on the drag itself; null means "nothing of ours came
    // over the wire", and the drop handler falls back to its own drag state.
    const droppedAlbum = parseAlbumDrag(e.dataTransfer.getData(ALBUM_DRAG_MIME))

    onAlbumDrop(droppedAlbum, album.uuid)
  }

  const handleCardDoubleClick = () => {
    if (!isRenaming && !isChangingDate && onDoubleClick) {
      onDoubleClick()
    }
  }

  // Build class names
  const className = [
    'album-card',
    isDragging && draggedAlbum?.uuid === album.uuid ? 'album-card--dragging' : '',
    isDragging && draggedAlbum?.uuid !== album.uuid ? 'album-card--drag-target' : '',
    isRenaming ? 'album-card--editing' : '',
    isChangingDate ? 'album-card--editing' : '',
    isEmpty ? 'album-card--empty' : '',
  ].filter(Boolean).join(' ')

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return 'Invalid date'
    }
  }

  return (
    <div 
      className={className}
      draggable={!isRenaming && !isChangingDate}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onDoubleClick={handleCardDoubleClick}
    >
      <div className={`album-thumbnail${coverUrl && coverLoaded ? ' album-thumbnail--loaded' : ''}`}>
        {coverUrl ? (
          <>
            <div className="album-cover-loader" aria-hidden="true">
              <span
                className="album-cover-mark"
                style={{ ['--cover-mask' as string]: `url(${memoarLogo})` } as React.CSSProperties}
              />
            </div>
            <img
              ref={coverImgRef}
              className="album-thumbnail-img"
              src={coverUrl}
              alt={album.name}
              loading="lazy"
              onLoad={() => setCoverLoaded(true)}
            />
          </>
        ) : isEmpty ? (
          <Upload size={32} />
        ) : (
          <Camera size={32} />
        )}
      </div>
      
      <div className="album-details">
        <div className="album-name-container">
          {isRenaming ? (
            <form onSubmit={handleRenameSubmit} onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onBlur={handleRenameCancel}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    handleRenameCancel()
                  }
                  e.stopPropagation()
                }}
                className="album-name-input"
                autoFocus
                maxLength={50}
              />
            </form>
          ) : (
            <>
              <h3 className="album-name">{album.name}</h3>
              {onRename && isOwner && (
                <button 
                  className="album-inline-edit-button album-edit-name"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRename()
                  }}
                  aria-label="Rename album"
                >
                  <Edit2 size={12} />
                </button>
              )}
            </>
          )}
        </div>
        
        <div className="album-date-container">
          {isChangingDate ? (
            <form onSubmit={handleDateSubmit} onClick={(e) => e.stopPropagation()}>
              <input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                onBlur={handleDateBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    handleDateCancel()
                  }
                  e.stopPropagation()
                }}
                className="album-date-input"
                autoFocus
              />
            </form>
          ) : (
            <>
              <div className="album-date">
                <Calendar size={12} />
                <span>{formatDate(album.createdAt)}</span>
              </div>
              {onChangeDate && isOwner && (
                <button 
                  className="album-inline-edit-button album-edit-date"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleChangeDate()
                  }}
                  aria-label="Change album date"
                >
                  <Calendar size={12} />
                </button>
              )}
            </>
          )}
        </div>
        
        {isDevelopment && isEmpty && (
          <span className="album-badge album-badge--virtual">Empty</span>
        )}
        
        {isDevelopment && isTempAlbum && (
          <span className="album-badge album-badge--local">Local Only</span>
        )}
      </div>

      {/* Always show menu for owners, or if delete is available */}
      {(isOwner || onDelete) && (
        <div className="album-menu-container" ref={menuRef}>
          <button 
            className="album-menu-trigger" 
            onClick={handleMenuClick}
            aria-label="Album options"
          >
            <MoreVertical size={14} />
          </button>
          
          {showMenu && (
            <div className="album-menu">
              {onShare && isOwner && (
                <button 
                  className={`album-menu-item ${isTempAlbum ? 'album-menu-item--disabled' : ''}`}
                  onClick={isTempAlbum ? undefined : handleShare}
                  disabled={isTempAlbum}
                  title={isTempAlbum ? 'Upload photos to this album first to enable sharing' : 'Share Album'}
                >
                  <Share2 size={14} />
                  <span>Share</span>
                </button>
              )}
              {/*
                Download sits with Share because both hand the album to
                someone -- and deliberately ABOVE Delete, so the menu runs
                benign-to-destructive and the irreversible item stays last.
                Disabled for temp albums for the same reason Share is: nothing
                has been written to the server yet, so there is no archive to
                build.
              */}
              {onDownload && (
                <button
                  className={`album-menu-item ${isTempAlbum ? 'album-menu-item--disabled' : ''}`}
                  onClick={isTempAlbum ? undefined : handleDownload}
                  disabled={isTempAlbum}
                  title={isTempAlbum ? 'Save this album before downloading it' : 'Download the whole album'}
                >
                  <FolderDown size={14} />
                  <span>Download</span>
                </button>
              )}
              {onDelete && (
                <button className="album-menu-item album-menu-item--danger" onClick={onDelete}>
                  <Trash2 size={14} />
                  <span>Delete</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
} 